import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebaseConfig';
import { collection, getDocs, addDoc, deleteDoc, doc, query, orderBy, where, getDoc, setDoc } from 'firebase/firestore';
import { showToast } from '../components/Toast';
import '../css/blog.css';

// Extract SoundCloud URL info (based on API guide)
const extractSoundCloudInfo = (url) => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    
    // Match patterns like /artist/track or /artist/sets/playlist
    const match = pathname.match(/\/([^\/?]+)(?:\/sets)?\/([^\/?]+)/);
    
    if (match) {
      const [, artist, resource] = match;
      const isPlaylist = url.includes('/sets/');
      
      return {
        artist,
        resource,
        type: isPlaylist ? 'playlist' : 'track',
        valid: true
      };
    }
    
    return { valid: false };
  } catch (error) {
    return { valid: false };
  }
};

// Validate SoundCloud URL format (improved per SoundCloud API guide)
const isValidSoundCloudUrl = (url) => {
  if (!url || !url.includes('soundcloud.com')) return false;
  
  // Must have track or playlist structure
  return /soundcloud\.com\/[\w-]+\/?[\w-]*/.test(url);
};

function Blog() {
  const [userRole, setUserRole] = useState(null);
  const [user, setUser] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [tracksOfDay, setTracksOfDay] = useState([]);
  const [tracksOfMonth, setTracksOfMonth] = useState([]);
  const [tracksOfYear, setTracksOfYear] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  
  // Form states for admin
  const [playlistUrl, setPlaylistUrl] = useState('');
  const [playlistTitle, setPlaylistTitle] = useState('');
  const [playlistDesc, setPlaylistDesc] = useState('');
  const [playlistPreview, setPlaylistPreview] = useState(null);
  
  const [trackUrl, setTrackUrl] = useState('');
  const [trackTitle, setTrackTitle] = useState('');
  const [trackDesc, setTrackDesc] = useState('');
  const [trackType, setTrackType] = useState('month');
  const [trackPreview, setTrackPreview] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [editingTrackId, setEditingTrackId] = useState(null);
  const [editTrackUrl, setEditTrackUrl] = useState('');
  const [editTrackTitle, setEditTrackTitle] = useState('');
  const [editTrackDesc, setEditTrackDesc] = useState('');

  // Carousel indices for each track type
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
  const [currentYearIndex, setCurrentYearIndex] = useState(0);

  // Likes and Comments
  const [trackLikes, setTrackLikes] = useState({});
  const [trackComments, setTrackComments] = useState({});
  const [userLikes, setUserLikes] = useState({});
  const [newCommentText, setNewCommentText] = useState({});

  // Comments Modal
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [selectedTrackForComments, setSelectedTrackForComments] = useState(null);

  useEffect(() => {
    const currentUser = auth.currentUser;
    setUser(currentUser);
    fetchUserRole();
  }, []);

  // Cargar contenido del blog cuando el componente monte
  useEffect(() => {
    fetchBlogContent();
  }, []);

  // Reload likes when user changes or tracks load
  useEffect(() => {
    if (user && (tracksOfDay.length > 0 || tracksOfMonth.length > 0 || tracksOfYear.length > 0)) {
      const allTracks = [...tracksOfDay, ...tracksOfMonth, ...tracksOfYear];
      allTracks.forEach(track => {
        loadTrackLikes(track.id);
      });
    }
  }, [user, tracksOfDay, tracksOfMonth, tracksOfYear]);

  // Additional effect to ensure month and year tracks are loaded on page reload
  useEffect(() => {
    if (user && !loading && (tracksOfMonth.length > 0 || tracksOfYear.length > 0)) {
      setTimeout(() => {
        tracksOfMonth.forEach(track => loadTrackLikes(track.id));
        tracksOfYear.forEach(track => loadTrackLikes(track.id));
      }, 300);
    }
  }, [user, loading]);

  const fetchUserRole = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          setUserRole(userDoc.data().role);
        }
      }
    } catch (error) {
      console.error('Error getting user role:', error);
    }
  };

  const fetchBlogContent = async () => {
    try {
      setLoading(true);

      // Fetch Playlists
      const playlistsQuery = query(
        collection(db, 'blog_playlists'),
        orderBy('createdAt', 'desc')
      );
      const playlistsSnap = await getDocs(playlistsQuery);
      const playlistsData = playlistsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlaylists(playlistsData);

      // Fetch All Tracks (day, month, year)
      const tracksQuery = query(
        collection(db, 'blog_tracks'),
        orderBy('createdAt', 'desc')
      );
      const tracksSnap = await getDocs(tracksQuery);
      const tracksData = tracksSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Separate tracks by type
      const dayTracks = tracksData.filter(t => t.type === 'day');
      const monthTracks = tracksData.filter(t => t.type === 'month');
      const yearTracks = tracksData.filter(t => t.type === 'year');

      setTracksOfDay(dayTracks);
      setTracksOfMonth(monthTracks);
      setTracksOfYear(yearTracks);

      // Load likes and comments for all tracks
      const allTracks = [...dayTracks, ...monthTracks, ...yearTracks];
      for (const track of allTracks) {
        await loadTrackLikes(track.id);
        await loadTrackComments(track.id);
      }

      showToast('✅ Blog content loaded', 'success', true);
    } catch (error) {
      console.error('Error fetching blog content:', error);
      showToast('❌ Error loading blog content', 'error', true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlaylist = async (e) => {
    e.preventDefault();

    if (!playlistUrl || !playlistTitle) {
      showToast('⚠️ Please fill in all required fields', 'warning', true);
      return;
    }

    if (!playlistPreview) {
      showToast('❌ Please validate the SoundCloud URL first', 'error', true);
      return;
    }

    try {
      setSubmitting(true);
      await addDoc(collection(db, 'blog_playlists'), {
        url: playlistUrl,
        title: playlistTitle,
        description: playlistDesc,
        uploadedBy: auth.currentUser.uid,
        createdAt: new Date()
      });

      showToast('✅ Playlist added successfully', 'success', true);
      setPlaylistUrl('');
      setPlaylistTitle('');
      setPlaylistDesc('');
      setPlaylistPreview(null);
      fetchBlogContent();
    } catch (error) {
      console.error('Error adding playlist:', error);
      showToast('❌ Error adding playlist: ' + error.message, 'error', true);
    } finally {
      setSubmitting(false);
    }
  };

  // Validate playlist URL and extract info
  const validatePlaylistUrl = async (url) => {
    setValidating(true);
    
    if (!url || !isValidSoundCloudUrl(url)) {
      showToast('❌ Invalid SoundCloud URL format', 'warning', true);
      setPlaylistPreview(null);
      setValidating(false);
      return;
    }

    const info = extractSoundCloudInfo(url);
    
    if (!info.valid || info.type !== 'playlist') {
      showToast('❌ Please provide a SoundCloud playlist URL', 'warning', true);
      setPlaylistPreview(null);
      setValidating(false);
      return;
    }

    // Show preview info
    setPlaylistPreview({
      artist: info.artist,
      resource: info.resource,
      type: 'playlist',
      url: url
    });

    // Auto-fill title from URL if empty
    if (!playlistTitle) {
      const suggestedTitle = info.resource.replace(/-/g, ' ').toUpperCase();
      setPlaylistTitle(suggestedTitle);
    }

    setValidating(false);
  };

  // Validate track URL
  const validateTrackUrl = async (url) => {
    setValidating(true);
    
    if (!url || !isValidSoundCloudUrl(url)) {
      showToast('❌ Invalid SoundCloud URL format', 'warning', true);
      setTrackPreview(null);
      setValidating(false);
      return;
    }

    const info = extractSoundCloudInfo(url);
    
    if (!info.valid || info.type !== 'track') {
      showToast('❌ Please provide a SoundCloud track URL', 'warning', true);
      setTrackPreview(null);
      setValidating(false);
      return;
    }

    setTrackPreview({
      artist: info.artist,
      resource: info.resource,
      type: 'track',
      url: url
    });

    if (!trackTitle) {
      const suggestedTitle = info.resource.replace(/-/g, ' ').toUpperCase();
      setTrackTitle(suggestedTitle);
    }

    setValidating(false);
  };

  const handleAddTrack = async (e) => {
    e.preventDefault();

    if (!trackUrl || !trackTitle) {
      showToast('⚠️ Please fill in all required fields', 'warning', true);
      return;
    }

    if (!trackPreview) {
      showToast('❌ Please validate the SoundCloud URL first', 'error', true);
      return;
    }

    try {
      setSubmitting(true);

      // Add new track (allow multiple tracks per type)
      await addDoc(collection(db, 'blog_tracks'), {
        url: trackUrl,
        title: trackTitle,
        description: trackDesc,
        type: trackType,
        uploadedBy: auth.currentUser.uid,
        createdAt: new Date()
      });

      showToast(`✅ Track of the ${trackType} added successfully`, 'success', true);
      setTrackUrl('');
      setTrackTitle('');
      setTrackDesc('');
      setTrackPreview(null);
      fetchBlogContent();
    } catch (error) {
      console.error('Error adding track:', error);
      showToast('❌ Error adding track: ' + error.message, 'error', true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePlaylist = async (id) => {
    if (window.confirm('Are you sure you want to delete this playlist?')) {
      try {
        await deleteDoc(doc(db, 'blog_playlists', id));
        showToast('✅ Playlist deleted successfully', 'success', true);
        fetchBlogContent();
      } catch (error) {
        console.error('Error deleting playlist:', error);
        showToast('❌ Error deleting playlist', 'error', true);
      }
    }
  };
  const handleEditTrack = (track) => {
    setEditingTrackId(track.id);
    setEditTrackUrl(track.url);
    setEditTrackTitle(track.title);
    setEditTrackDesc(track.description || '');
  };

  const handleUpdateTrack = async (e) => {
    e.preventDefault();

    if (!editTrackUrl || !editTrackTitle) {
      showToast('⚠️ Please fill in all required fields', 'warning', true);
      return;
    }

    try {
      setSubmitting(true);

      // Find the track type
      let trackTypeFound = 'month';
      if (tracksOfDay.find(t => t.id === editingTrackId)) trackTypeFound = 'day';
      else if (tracksOfMonth.find(t => t.id === editingTrackId)) trackTypeFound = 'month';
      else if (tracksOfYear.find(t => t.id === editingTrackId)) trackTypeFound = 'year';

      // Delete old track and add new one with same type
      await deleteDoc(doc(db, 'blog_tracks', editingTrackId));

      await addDoc(collection(db, 'blog_tracks'), {
        url: editTrackUrl,
        title: editTrackTitle,
        description: editTrackDesc,
        type: trackTypeFound,
        uploadedBy: auth.currentUser.uid,
        createdAt: new Date()
      });

      showToast(`✅ Track of the ${trackTypeFound} updated successfully`, 'success', true);
      setEditingTrackId(null);
      setEditTrackUrl('');
      setEditTrackTitle('');
      setEditTrackDesc('');
      fetchBlogContent();
    } catch (error) {
      console.error('Error updating track:', error);
      showToast('❌ Error updating track: ' + error.message, 'error', true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTrack = async (id) => {
    if (window.confirm('Are you sure you want to delete this track?')) {
      try {
        await deleteDoc(doc(db, 'blog_tracks', id));
        showToast('✅ Track deleted successfully', 'success', true);
        fetchBlogContent();
      } catch (error) {
        console.error('Error deleting track:', error);
        showToast('❌ Error deleting track', 'error', true);
      }
    }
  };

  // Carousel navigation functions
  const goToPreviousDayTrack = () => {
    setCurrentDayIndex((prev) => (prev === 0 ? tracksOfDay.length - 1 : prev - 1));
  };

  const goToNextDayTrack = () => {
    setCurrentDayIndex((prev) => (prev === tracksOfDay.length - 1 ? 0 : prev + 1));
  };

  const goToPreviousMonthTrack = () => {
    setCurrentMonthIndex((prev) => (prev === 0 ? tracksOfMonth.length - 1 : prev - 1));
  };

  const goToNextMonthTrack = () => {
    setCurrentMonthIndex((prev) => (prev === tracksOfMonth.length - 1 ? 0 : prev + 1));
  };

  const goToPreviousYearTrack = () => {
    setCurrentYearIndex((prev) => (prev === 0 ? tracksOfYear.length - 1 : prev - 1));
  };

  const goToNextYearTrack = () => {
    setCurrentYearIndex((prev) => (prev === tracksOfYear.length - 1 ? 0 : prev + 1));
  };

  // Like/Unlike Track
  const handleLikeTrack = async (trackId) => {
    if (!user) {
      showToast('⚠️ Please login to like tracks', 'warning', true);
      return;
    }

    try {
      const likeRef = doc(db, 'track_likes', `${trackId}_${user.uid}`);
      const likeDoc = await getDoc(likeRef);

      if (likeDoc.exists()) {
        // Unlike - delete the like
        await deleteDoc(likeRef);
        setUserLikes(prev => {
          const updated = { ...prev };
          delete updated[trackId];
          return updated;
        });
      } else {
        // Like - create new like with custom ID to prevent duplicates
        await setDoc(likeRef, {
          trackId,
          userId: user.uid,
          userName: user.displayName || 'Anonymous',
          createdAt: new Date()
        });
        setUserLikes(prev => ({ ...prev, [trackId]: true }));
      }

      // Reload likes count
      loadTrackLikes(trackId);
    } catch (error) {
      console.error('Error liking track:', error);
      showToast('❌ Error liking track', 'error', true);
    }
  };

  // Load likes for a track
  const loadTrackLikes = async (trackId) => {
    try {
      const q = query(collection(db, 'track_likes'), where('trackId', '==', trackId));
      const snapshot = await getDocs(q);
      setTrackLikes(prev => ({ ...prev, [trackId]: snapshot.size }));

      // Check if user likes this track
      if (user) {
        const likeRef = doc(db, 'track_likes', `${trackId}_${user.uid}`);
        const likeDoc = await getDoc(likeRef);
        setUserLikes(prev => {
          const updated = { ...prev };
          if (likeDoc.exists()) {
            updated[trackId] = true;
          } else {
            updated[trackId] = false;
          }
          return updated;
        });
      } else {
        setUserLikes(prev => {
          const updated = { ...prev };
          updated[trackId] = false;
          return updated;
        });
      }
    } catch (error) {
      console.error('Error loading likes:', error);
    }
  };

  // Add comment
  const handleAddComment = async (trackId) => {
    if (!user) {
      showToast('⚠️ Please login to comment', 'warning', true);
      return;
    }

    const commentText = newCommentText[trackId]?.trim();
    if (!commentText) {
      showToast('⚠️ Please write a comment', 'warning', true);
      return;
    }

    try {
      await addDoc(collection(db, 'track_comments'), {
        trackId,
        userId: user.uid,
        userName: user.displayName || 'Anonymous',
        userEmail: user.email,
        text: commentText,
        createdAt: new Date()
      });

      setNewCommentText(prev => ({ ...prev, [trackId]: '' }));
      showToast('✅ Comment added', 'success', true);
      loadTrackComments(trackId);
    } catch (error) {
      console.error('Error adding comment:', error);
      showToast('❌ Error adding comment', 'error', true);
    }
  };

  // Load comments for a track
  const loadTrackComments = async (trackId) => {
    try {
      const q = query(
        collection(db, 'track_comments'),
        where('trackId', '==', trackId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const comments = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTrackComments(prev => ({ ...prev, [trackId]: comments }));
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  // Delete comment
  const handleDeleteComment = async (commentId, trackId) => {
    if (!user) return;

    try {
      await deleteDoc(doc(db, 'track_comments', commentId));
      showToast('✅ Comment deleted', 'success', true);
      loadTrackComments(trackId);
    } catch (error) {
      console.error('Error deleting comment:', error);
      showToast('❌ Error deleting comment', 'error', true);
    }
  };

  // Open comments modal for a specific track
  const openCommentsModal = (track) => {
    setSelectedTrackForComments(track);
    setShowCommentsModal(true);
  };

  // Close comments modal
  const closeCommentsModal = () => {
    setShowCommentsModal(false);
    setSelectedTrackForComments(null);
  };

  const isValidSoundCloudUrl = (url) => {
    return url.includes('soundcloud.com');
  };

  if (loading) {
    return (
      <div className="blog-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading blog...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-container">
      {/* ADMIN SECTION */}
      {(userRole === 'admin' || userRole === 'superadmin') && (
        <section className="admin-blog-section">
          <h2 className="admin-section-title">📻 Blog Administration</h2>

          {/* Add Playlist Form */}
          <div className="admin-form-container">
            <h3>➕ Add Playlist</h3>
            <form onSubmit={handleAddPlaylist} className="blog-form">
              <div className="form-group">
                <label>SoundCloud Playlist URL *</label>
                <div className="url-input-wrapper">
                  <input
                    type="text"
                    placeholder="https://soundcloud.com/artist/sets/playlist-name"
                    value={playlistUrl}
                    onChange={(e) => setPlaylistUrl(e.target.value)}
                    onBlur={() => playlistUrl && validatePlaylistUrl(playlistUrl)}
                    disabled={submitting || validating}
                  />
                  {validating && <span className="validating">🔄 Validating...</span>}
                </div>
                {playlistPreview && (
                  <div className="preview-info">
                    ✅ Valid Playlist: <strong>{playlistPreview.artist}</strong> / {playlistPreview.resource}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Playlist Title *</label>
                <input
                  type="text"
                  placeholder="Enter playlist title"
                  value={playlistTitle}
                  onChange={(e) => setPlaylistTitle(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  placeholder="Enter playlist description"
                  value={playlistDesc}
                  onChange={(e) => setPlaylistDesc(e.target.value)}
                  disabled={submitting}
                  rows="3"
                />
              </div>

              <button type="submit" className="btn-submit" disabled={submitting || !playlistPreview}>
                {submitting ? '⏳ Adding...' : '➕ Add Playlist'}
              </button>
            </form>
          </div>

          {/* Add Track Form */}
          <div className="admin-form-container">
            <h3>🎵 Add Track (Day/Month/Year)</h3>
            <form onSubmit={handleAddTrack} className="blog-form">
              <div className="form-group">
                <label>Track Type *</label>
                <select
                  value={trackType}
                  onChange={(e) => setTrackType(e.target.value)}
                  disabled={submitting}
                >
                  <option value="day">Track of the Day</option>
                  <option value="month">Track of the Month</option>
                  <option value="year">Track of the Year</option>
                </select>
              </div>

              <div className="form-group">
                <label>SoundCloud Track URL *</label>
                <div className="url-input-wrapper">
                  <input
                    type="text"
                    placeholder="https://soundcloud.com/artist/track-name"
                    value={trackUrl}
                    onChange={(e) => setTrackUrl(e.target.value)}
                    onBlur={() => trackUrl && validateTrackUrl(trackUrl)}
                    disabled={submitting || validating}
                  />
                  {validating && <span className="validating">🔄 Validating...</span>}
                </div>
                {trackPreview && (
                  <div className="preview-info">
                    ✅ Valid Track: <strong>{trackPreview.artist}</strong> / {trackPreview.resource}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Track Title *</label>
                <input
                  type="text"
                  placeholder="Enter track title"
                  value={trackTitle}
                  onChange={(e) => setTrackTitle(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  placeholder="Enter track description or notes"
                  value={trackDesc}
                  onChange={(e) => setTrackDesc(e.target.value)}
                  disabled={submitting}
                  rows="3"
                />
              </div>

              <button type="submit" className="btn-submit" disabled={submitting || !trackPreview}>
                {submitting ? '⏳ Adding...' : '🎵 Add Track'}
              </button>
            </form>
          </div>

          {/* Info box about SoundCloud API */}
          <div className="info-box">
            <h4>💡 Tip: Getting SoundCloud Links</h4>
            <p>
              1. Go to <strong>soundcloud.com</strong> and find your playlist or track<br/>
              2. Click <strong>Share</strong> and copy the URL<br/>
              3. Paste it above and click outside to validate<br/>
              The form will auto-fill the title if left empty
            </p>
          </div>
        </section>
      )}

      {/* CLIENT SECTION - Display Content */}
      <section className="blog-content-section">
        {/* Edit Track Modal */}
        {editingTrackId && (
          <div className="edit-track-modal">
            <div className="modal-content">
              <h3>Edit Track</h3>
              <form onSubmit={handleUpdateTrack} className="blog-form">
                <div className="form-group">
                  <label>SoundCloud Track URL *</label>
                  <input
                    type="text"
                    placeholder="https://soundcloud.com/artist/track-name"
                    value={editTrackUrl}
                    onChange={(e) => setEditTrackUrl(e.target.value)}
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label>Track Title *</label>
                  <input
                    type="text"
                    placeholder="Enter track title"
                    value={editTrackTitle}
                    onChange={(e) => setEditTrackTitle(e.target.value)}
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    placeholder="Enter track description or notes"
                    value={editTrackDesc}
                    onChange={(e) => setEditTrackDesc(e.target.value)}
                    disabled={submitting}
                    rows="3"
                  />
                </div>

                <div className="modal-buttons">
                  <button type="submit" className="btn-submit" disabled={submitting}>
                    {submitting ? '⏳ Updating...' : '💾 Update Track'}
                  </button>
                  <button 
                    type="button" 
                    className="btn-cancel" 
                    onClick={() => setEditingTrackId(null)}
                    disabled={submitting}
                  >
                    ✕ Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* Playlists */}
        {playlists.length > 0 && (
          <div className="playlists-section">
            <div className="playlists-grid">
              {playlists.map((playlist) => (
                <div key={playlist.id} className="playlist-card">
                  <div className="playlist-header">
                    <h3>{playlist.title}</h3>
                    {(userRole === 'admin' || userRole === 'superadmin') && (
                      <button
                        className="btn-delete-playlist"
                        onClick={() => handleDeletePlaylist(playlist.id)}
                        title="Delete playlist"
                      >
                        🗑️
                      </button>
                    )}
                  </div>

                  {playlist.description && (
                    <p className="playlist-description">{playlist.description}</p>
                  )}

                  <div className="soundcloud-embed">
                    <iframe
                      width="100%"
                      height="400"
                      scrolling="no"
                      frameBorder="no"
                      allow="autoplay"
                      src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(
                        playlist.url
                      )}&color=%23FFD700&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`}
                    ></iframe>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Track of the Day */}
        {tracksOfDay.length > 0 && (
          <div className="featured-tracks-section">
            <h3 className="track-type-title">☀️ Track of the Day</h3>
            <div className="carousel-container">
              {tracksOfDay.length > 1 && (
                <button className="carousel-btn carousel-btn-prev" onClick={goToPreviousDayTrack}>
                  ❮
                </button>
              )}
              <div className="featured-track-card">
                {(() => {
                  const track = tracksOfDay[currentDayIndex];
                  return (
                    <>
                      <div className="track-header-actions">
                        <div className="track-badge">☀️ Day</div>
                        {(userRole === 'admin' || userRole === 'superadmin') && (
                          <div className="track-actions">
                            <button
                              className="btn-edit-track"
                              onClick={() => handleEditTrack(track)}
                              title="Edit track"
                            >
                              ✏️
                            </button>
                            <button
                              className="btn-delete-track"
                              onClick={() => handleDeleteTrack(track.id)}
                              title="Delete track"
                            >
                              🗑️
                            </button>
                          </div>
                        )}
                      </div>
                      <h3>{track.title}</h3>
                      {track.description && (
                        <p className="track-description">{track.description}</p>
                      )}
                      <div className="soundcloud-embed">
                        <iframe
                          width="100%"
                          height="180"
                          scrolling="no"
                          frameBorder="no"
                          allow="autoplay"
                          src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(
                            track.url
                          )}&color=%23FFD700&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`}
                        ></iframe>
                      </div>

                      {/* Likes and Comments Section */}
                      <div className="track-interactions">
                        {/* Like Button */}
                        <button
                          className={`btn-like ${userLikes[track.id] ? 'liked' : ''}`}
                          onClick={() => {
                            handleLikeTrack(track.id);
                            loadTrackLikes(track.id);
                          }}
                          title={user ? (userLikes[track.id] ? 'Unlike' : 'Like') : 'Login to like'}
                        >
                          {userLikes[track.id] ? '❤️' : '🤍'} {trackLikes[track.id] || 0}
                        </button>

                        {/* Comments Count - Click to Open Modal */}
                        <button 
                          className="comments-count-btn"
                          onClick={() => openCommentsModal(track)}
                          title="View all comments"
                        >
                          💬 {(trackComments[track.id] || []).length}
                        </button>
                      </div>

                      {/* Comments Section */}
                      <div className="track-comments-section">
                        <h4>Comments</h4>

                        {/* Add Comment Form */}
                        {user ? (
                          <div className="comment-form">
                            <input
                              type="text"
                              placeholder="Add a comment..."
                              value={newCommentText[track.id] || ''}
                              onChange={(e) =>
                                setNewCommentText(prev => ({
                                  ...prev,
                                  [track.id]: e.target.value
                                }))
                              }
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleAddComment(track.id);
                                }
                              }}
                              className="comment-input"
                            />
                            <button
                              className="btn-comment-submit"
                              onClick={() => handleAddComment(track.id)}
                            >
                              Send
                            </button>
                          </div>
                        ) : (
                          <p className="login-prompt">Login to add comments</p>
                        )}

                        {/* Display Comments */}
                        <div className="comments-list">
                          {(trackComments[track.id] || []).length === 0 ? (
                            <p className="no-comments">No comments yet</p>
                          ) : (
                            (trackComments[track.id] || []).map(comment => (
                              <div key={comment.id} className="comment-item">
                                <div className="comment-header">
                                  <strong>{comment.userName}</strong>
                                  <span className="comment-date">
                                    {new Date(comment.createdAt?.toDate?.() || comment.createdAt).toLocaleDateString()}
                                  </span>
                                </div>
                                <p className="comment-text">{comment.text}</p>
                                {(user?.uid === comment.userId || userRole === 'admin' || userRole === 'superadmin') && (
                                  <button
                                    className="btn-delete-comment"
                                    onClick={() => handleDeleteComment(comment.id, track.id)}
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {tracksOfDay.length > 1 && (
                        <div className="carousel-indicator">
                          {currentDayIndex + 1} of {tracksOfDay.length}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              {tracksOfDay.length > 1 && (
                <button className="carousel-btn carousel-btn-next" onClick={goToNextDayTrack}>
                  ❯
                </button>
              )}
            </div>
          </div>
        )}

        {/* Track of the Month */}
        {tracksOfMonth.length > 0 && (
          <div className="featured-tracks-section">
            <h3 className="track-type-title">🏆 Track of the Month</h3>
            <div className="carousel-container">
              {tracksOfMonth.length > 1 && (
                <button className="carousel-btn carousel-btn-prev" onClick={goToPreviousMonthTrack}>
                  ❮
                </button>
              )}
              <div className="featured-track-card">
                {(() => {
                  const track = tracksOfMonth[currentMonthIndex];
                  return (
                    <>
                      <div className="track-header-actions">
                        <div className="track-badge">🏆 Month</div>
                        {(userRole === 'admin' || userRole === 'superadmin') && (
                          <div className="track-actions">
                            <button
                              className="btn-edit-track"
                              onClick={() => handleEditTrack(track)}
                              title="Edit track"
                            >
                              ✏️
                            </button>
                            <button
                              className="btn-delete-track"
                              onClick={() => handleDeleteTrack(track.id)}
                              title="Delete track"
                            >
                              🗑️
                            </button>
                          </div>
                        )}
                      </div>
                      <h3>{track.title}</h3>
                      {track.description && (
                        <p className="track-description">{track.description}</p>
                      )}
                      <div className="soundcloud-embed">
                        <iframe
                          width="100%"
                          height="180"
                          scrolling="no"
                          frameBorder="no"
                          allow="autoplay"
                          src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(
                            track.url
                          )}&color=%23FFD700&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`}
                        ></iframe>
                      </div>

                      {/* Likes and Comments Section */}
                      <div className="track-interactions">
                        {/* Like Button */}
                        <button
                          className={`btn-like ${userLikes[track.id] ? 'liked' : ''}`}
                          onClick={() => {
                            handleLikeTrack(track.id);
                            loadTrackLikes(track.id);
                          }}
                          title={user ? (userLikes[track.id] ? 'Unlike' : 'Like') : 'Login to like'}
                        >
                          {userLikes[track.id] ? '❤️' : '🤍'} {trackLikes[track.id] || 0}
                        </button>

                        {/* Comments Count - Click to Open Modal */}
                        <button 
                          className="comments-count-btn"
                          onClick={() => openCommentsModal(track)}
                          title="View all comments"
                        >
                          💬 {(trackComments[track.id] || []).length}
                        </button>
                      </div>

                      {/* Comments Section */}
                      <div className="track-comments-section">
                        <h4>Comments</h4>

                        {/* Add Comment Form */}
                        {user ? (
                          <div className="comment-form">
                            <input
                              type="text"
                              placeholder="Add a comment..."
                              value={newCommentText[track.id] || ''}
                              onChange={(e) =>
                                setNewCommentText(prev => ({
                                  ...prev,
                                  [track.id]: e.target.value
                                }))
                              }
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleAddComment(track.id);
                                }
                              }}
                              className="comment-input"
                            />
                            <button
                              className="btn-comment-submit"
                              onClick={() => handleAddComment(track.id)}
                            >
                              Send
                            </button>
                          </div>
                        ) : (
                          <p className="login-prompt">Login to add comments</p>
                        )}

                        {/* Display Comments */}
                        <div className="comments-list">
                          {(trackComments[track.id] || []).length === 0 ? (
                            <p className="no-comments">No comments yet</p>
                          ) : (
                            (trackComments[track.id] || []).map(comment => (
                              <div key={comment.id} className="comment-item">
                                <div className="comment-header">
                                  <strong>{comment.userName}</strong>
                                  <span className="comment-date">
                                    {new Date(comment.createdAt?.toDate?.() || comment.createdAt).toLocaleDateString()}
                                  </span>
                                </div>
                                <p className="comment-text">{comment.text}</p>
                                {(user?.uid === comment.userId || userRole === 'admin' || userRole === 'superadmin') && (
                                  <button
                                    className="btn-delete-comment"
                                    onClick={() => handleDeleteComment(comment.id, track.id)}
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {tracksOfMonth.length > 1 && (
                        <div className="carousel-indicator">
                          {currentMonthIndex + 1} of {tracksOfMonth.length}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              {tracksOfMonth.length > 1 && (
                <button className="carousel-btn carousel-btn-next" onClick={goToNextMonthTrack}>
                  ❯
                </button>
              )}
            </div>
          </div>
        )}

        {/* Track of the Year */}
        {tracksOfYear.length > 0 && (
          <div className="featured-tracks-section">
            <h3 className="track-type-title">🌟 Track of the Year</h3>
            <div className="carousel-container">
              {tracksOfYear.length > 1 && (
                <button className="carousel-btn carousel-btn-prev" onClick={goToPreviousYearTrack}>
                  ❮
                </button>
              )}
              <div className="featured-track-card">
                {(() => {
                  const track = tracksOfYear[currentYearIndex];
                  return (
                    <>
                      <div className="track-header-actions">
                        <div className="track-badge year">🌟 Year</div>
                        {(userRole === 'admin' || userRole === 'superadmin') && (
                          <div className="track-actions">
                            <button
                              className="btn-edit-track"
                              onClick={() => handleEditTrack(track)}
                              title="Edit track"
                            >
                              ✏️
                            </button>
                            <button
                              className="btn-delete-track"
                              onClick={() => handleDeleteTrack(track.id)}
                              title="Delete track"
                            >
                              🗑️
                            </button>
                          </div>
                        )}
                      </div>
                      <h3>{track.title}</h3>
                      {track.description && (
                        <p className="track-description">{track.description}</p>
                      )}
                      <div className="soundcloud-embed">
                        <iframe
                          width="100%"
                          height="180"
                          scrolling="no"
                          frameBorder="no"
                          allow="autoplay"
                          src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(
                            track.url
                          )}&color=%23FFD700&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`}
                        ></iframe>
                      </div>

                      {/* Likes and Comments Section */}
                      <div className="track-interactions">
                        {/* Like Button */}
                        <button
                          className={`btn-like ${userLikes[track.id] ? 'liked' : ''}`}
                          onClick={() => {
                            handleLikeTrack(track.id);
                            loadTrackLikes(track.id);
                          }}
                          title={user ? (userLikes[track.id] ? 'Unlike' : 'Like') : 'Login to like'}
                        >
                          {userLikes[track.id] ? '❤️' : '🤍'} {trackLikes[track.id] || 0}
                        </button>

                        {/* Comments Count - Click to Open Modal */}
                        <button 
                          className="comments-count-btn"
                          onClick={() => openCommentsModal(track)}
                          title="View all comments"
                        >
                          💬 {(trackComments[track.id] || []).length}
                        </button>
                      </div>

                      {/* Comments Section */}
                      <div className="track-comments-section">
                        <h4>Comments</h4>

                        {/* Add Comment Form */}
                        {user ? (
                          <div className="comment-form">
                            <input
                              type="text"
                              placeholder="Add a comment..."
                              value={newCommentText[track.id] || ''}
                              onChange={(e) =>
                                setNewCommentText(prev => ({
                                  ...prev,
                                  [track.id]: e.target.value
                                }))
                              }
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleAddComment(track.id);
                                }
                              }}
                              className="comment-input"
                            />
                            <button
                              className="btn-comment-submit"
                              onClick={() => handleAddComment(track.id)}
                            >
                              Send
                            </button>
                          </div>
                        ) : (
                          <p className="login-prompt">Login to add comments</p>
                        )}

                        {/* Display Comments */}
                        <div className="comments-list">
                          {(trackComments[track.id] || []).length === 0 ? (
                            <p className="no-comments">No comments yet</p>
                          ) : (
                            (trackComments[track.id] || []).map(comment => (
                              <div key={comment.id} className="comment-item">
                                <div className="comment-header">
                                  <strong>{comment.userName}</strong>
                                  <span className="comment-date">
                                    {new Date(comment.createdAt?.toDate?.() || comment.createdAt).toLocaleDateString()}
                                  </span>
                                </div>
                                <p className="comment-text">{comment.text}</p>
                                {(user?.uid === comment.userId || userRole === 'admin' || userRole === 'superadmin') && (
                                  <button
                                    className="btn-delete-comment"
                                    onClick={() => handleDeleteComment(comment.id, track.id)}
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {tracksOfYear.length > 1 && (
                        <div className="carousel-indicator">
                          {currentYearIndex + 1} of {tracksOfYear.length}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              {tracksOfYear.length > 1 && (
                <button className="carousel-btn carousel-btn-next" onClick={goToNextYearTrack}>
                  ❯
                </button>
              )}
            </div>
          </div>
        )}

        {playlists.length === 0 && tracksOfDay.length === 0 && tracksOfMonth.length === 0 && tracksOfYear.length === 0 && (
          <div className="empty-state">
            <h2>📋 No content yet</h2>
            <p>The administrator has not published any playlists or tracks yet.</p>
          </div>
        )}

        {/* Comments Modal */}
        {showCommentsModal && selectedTrackForComments && (
          <div className="comments-modal-overlay" onClick={closeCommentsModal}>
            <div className="comments-modal-content" onClick={(e) => e.stopPropagation()}>
              {/* Left Side - Track Info and Player */}
              <div className="modal-left">
                <h2>{selectedTrackForComments.title}</h2>
                {selectedTrackForComments.description && (
                  <p className="modal-track-description">{selectedTrackForComments.description}</p>
                )}
                
                {/* SoundCloud Player */}
                <div className="modal-soundcloud-embed">
                  <iframe
                    width="100%"
                    height="300"
                    scrolling="no"
                    frameBorder="no"
                    allow="autoplay"
                    src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(
                      selectedTrackForComments.url
                    )}&color=%23FFD700&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`}
                  ></iframe>
                </div>

                {/* Track Interactions */}
                <div className="modal-track-interactions">
                  {/* Like Button */}
                  <button
                    className={`btn-like ${userLikes[selectedTrackForComments.id] ? 'liked' : ''}`}
                    onClick={() => {
                      handleLikeTrack(selectedTrackForComments.id);
                      loadTrackLikes(selectedTrackForComments.id);
                    }}
                    title={user ? (userLikes[selectedTrackForComments.id] ? 'Unlike' : 'Like') : 'Login to like'}
                  >
                    {userLikes[selectedTrackForComments.id] ? '❤️' : '🤍'} {trackLikes[selectedTrackForComments.id] || 0}
                  </button>
                </div>
              </div>

              {/* Right Side - Comments */}
              <div className="modal-right">
                <button className="modal-close-btn" onClick={closeCommentsModal}>✕</button>
                <h3 style={{ margin: 0, marginBottom: '20px' }}>Comments ({(trackComments[selectedTrackForComments.id] || []).length})</h3>

                {/* Add Comment Form */}
                {user ? (
                  <div className="modal-comment-form">
                    <input
                      type="text"
                      placeholder="Add a comment..."
                      value={newCommentText[selectedTrackForComments.id] || ''}
                      onChange={(e) =>
                        setNewCommentText(prev => ({
                          ...prev,
                          [selectedTrackForComments.id]: e.target.value
                        }))
                      }
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddComment(selectedTrackForComments.id);
                        }
                      }}
                      className="modal-comment-input"
                    />
                    <button
                      className="btn-comment-submit modal-submit"
                      onClick={() => handleAddComment(selectedTrackForComments.id)}
                    >
                      Send
                    </button>
                  </div>
                ) : (
                  <p className="login-prompt">Login to add comments</p>
                )}

                {/* Comments List */}
                <div className="modal-comments-list">
                  {(trackComments[selectedTrackForComments.id] || []).length === 0 ? (
                    <p className="no-comments">No comments yet. Be the first to comment!</p>
                  ) : (
                    (trackComments[selectedTrackForComments.id] || []).map(comment => (
                      <div key={comment.id} className="modal-comment-item">
                        <div className="modal-comment-header">
                          <strong>{comment.userName}</strong>
                          <span className="modal-comment-date">
                            {new Date(comment.createdAt?.toDate?.() || comment.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="modal-comment-text">{comment.text}</p>
                        {(user?.uid === comment.userId || userRole === 'admin' || userRole === 'superadmin') && (
                          <button
                            className="btn-delete-comment"
                            onClick={() => handleDeleteComment(comment.id, selectedTrackForComments.id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default Blog;
